import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import './preview.css'
import DesignPicker from './components/DesignPicker'
import LinePicker from './components/LinePicker'
import LevelPicker from './components/LevelPicker'
import View from './components/View'
import WelcomeModal from './components/WelcomeModal'
import { getPropertyById, getAssets, getPropertyByExternalId, getPropertyByUnitName } from '../../services'
import { getSafePropertyData } from '../../helpers/data'
import MobileHeader from './components/MobileHeader'
import { safeConfigurationOptions } from '../../helpers/general'
import Reset3DViewer from './components/Reset3DViewer'
import PackageInfo from './components/PackageInfo'
import Disclaimer from '../Sidebar/components/Disclaimer'
import MobileFooter from './components/MobileFooter'
import useDetectDevice from '../../hooks/useDetectDevice'
import { captureEvent } from '../../analytics'
import { analyticsEvents } from '../../analytics/events'
import { useStore } from '../../store'
import { useSendOnceWithDeps } from '../../analytics/hooks/useAnalytics'
import ModelsPrefetcher from '../ModelsPrefetcher'
import MobileSidebar from '../../components/MobileSidebar'
import ContactUs from './components/MobileHeader/components/ContactUs'
import { ReactComponent as CortLogo } from './icons/cort-logo.svg'
import { ReactComponent as EversetLogo } from './icons/everset-logo.svg'
import { AppConfigContext } from '../../context/AppConfig'
import VendorLogo from '../VendorLogo'
import Guide from './components/Guide'
import DebugControls from './components/DebugControls'
import PropertyLogo from '../PropertyLogo'
import VariantWrapper from '../../components/VariantWrapper'
import { variants } from '../../constants/variant'
import OfficesGallery from './components/Offices/OfficesGallery'
import MobileBottomSection from './components/MobileBottomSection'
import { getLineName } from '../../helpers/line'
import FullScreenToggle from './components/FullscreenToggle'
import PoweredBy from '../PoweredBy'
import SparxLogo from '../SparxLogo'
import ApplyNowButton, { showApplyNowButton } from './components/ApplyNow'
import ScreenType from '../ScreenType'
import AccessRestrictedModal from '../AccessRestrictedModal'
import TVDesignPicker from './components/TV/DesignPicker'
import TVReset3DViewer from './components/TV/Reset3DViewer'
import TVPackageInfo from './components/TV/PackageInfo'
import TVLinePicker from './components/TV/LinePicker'
import TVLevelPicker from './components/TV/LevelPicker'
import { isStagedWithCortPackage } from '../../helpers/packages'
import { debugTimeout } from '../../helpers/debug'

function Preview() {
  const {
    isBrodskyApp,
    isWideMode,
    isSWApp,
    isEWApp,
    isPDApp,
    property: property_id,
    external_property_id,
    line_id,
    package_id,
    show_welcome_modal,
    debug,
    single_line,
    subsequent_open,
    isOfficesApp,
    as_staging,
    hide_line,
    hide_vendor,
    hide_packages,
    isSFApp,
    mimicVariant,
    unit_num,
    unit_name,
    hide_disclaimer,
    hide_footer,
    isAvalonApp,
    set,
  } = useContext(AppConfigContext)

  const welcomeModalHiddenFromParams =
    show_welcome_modal === 'false' || as_staging || isOfficesApp || isBrodskyApp || isSFApp

  const [welcomeModalShown, setWelcomeModalShown] = useState(!welcomeModalHiddenFromParams ? true : false)

  const [property, setProperty] = useState(null)
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  const [selectedLine, setSelectedLine] = useState()
  const [selectedLevel, setSelectedLevel] = useState()
  const [selectedPackage, setSelectedPackage] = useState()
  const [selectedPlan, setSelectedPlan] = useState()

  const { isMobile, isMobileLandscape, isDesktop } = useDetectDevice()

  const setData = useStore((state) => state.setData)

  const packages = useMemo(() => {
    if (selectedLine && selectedLevel) {
      return selectedLine.packages.filter((p) => {
        return selectedLevel.design_layouts.find((d) => d.package?.id === p.id)
      })
    }
    return []
  }, [selectedLine, selectedLevel])

  useEffect(() => {
    const defaultPackage = package_id || (packages.find((p) => p.show_by_default) || packages[0] || {}).id
    if (!selectedPackage || !packages.find((p) => p.id === selectedPackage.id)) {
      setSelectedPackage(packages.find((p) => p.id === Number(defaultPackage)))
    }
  }, [selectedPackage, package_id, packages])

  //==========================================================================
  // Select default package
  //==========================================================================

  useEffect(() => {
    if ((property_id || external_property_id || unit_name) && !property && !fetchError) {
      ;(async () => {
        try {
        captureEvent(analyticsEvents.PAGE_LOADED)
        let data
        //==========================================================================
        // Fetch property data from API based on property_id, external_property_id, or unit_name
        //==========================================================================
        
        if (unit_name) {
          // First load with default_only = true for immediate use
          const res = await getPropertyByUnitName(unit_name, true)
          if (!res?.data) {
            setFetchError(true)
            setLoading(false)
            return
          }
          data = res.data.properties_by_unit_name

          // If no unit types returned, fetch all data instead
          if (!data || !data.unit_types || data.unit_types.length === 0) {
            const resAll = await getPropertyByUnitName(unit_name, false)
            data = resAll.data.properties_by_unit_name
          } else {
            data = {
              ...data,
              lines_preloading: true,
            }

            function loadAdditionalLines(){
              getPropertyByUnitName(unit_name, false).then((resAll) => {
                const allData = resAll.data.properties_by_unit_name
                const safeBgData = getSafePropertyData(allData)
                setProperty({
                  ...safeBgData,
                  lines_preloading: false,
                })
              })
            }
            if(debugTimeout){
              setTimeout(() => {
                loadAdditionalLines()
              }, debugTimeout);
            } else {
              loadAdditionalLines()
            }
          }
        } else if (external_property_id) {
          // First load with default_only = true for immediate use
          const res = await getPropertyByExternalId(external_property_id, true)
          if (!res?.data) {
            setFetchError(true)
            setLoading(false)
            return
          }
          data = res.data.properties_by_external_id

          // If no unit types returned, fetch all data instead
          if (!data || !data.unit_types || data.unit_types.length === 0) {
            const resAll = await getPropertyByExternalId(external_property_id, false)
            data = resAll.data.properties_by_external_id
          } else {
            data = {
              ...data,
              lines_preloading: true,
            }

            function loadAdditionalLines(){
              getPropertyByExternalId(external_property_id, false).then((resAll) => {
                const allData = resAll.data.properties_by_external_id
                const safeBgData = getSafePropertyData(allData)
                setProperty({
                  ...safeBgData,
                  lines_preloading: false,
                })
              })
            }
            if(debugTimeout){
              setTimeout(() => {
                loadAdditionalLines()
              }, debugTimeout);
            } else {
              loadAdditionalLines()
            }
          }
        } else {
          // First load with default_only = true for immediate use
          const res = await getPropertyById(property_id, true)
          if (!res?.data) {
            setFetchError(true)
            setLoading(false)
            return
          }
          data = res.data.properties_by_id

          // If no unit types returned, fetch all data instead
          if (!data || !data.unit_types || data.unit_types.length === 0) {
            const resAll = await getPropertyById(property_id, false)
            data = resAll.data.properties_by_id
          } else {
            data = {
              ...data,
              lines_preloading: true,
            }

            function loadAdditionalLines(){
              getPropertyById(property_id, false).then((resAll) => {
                const allData = resAll.data.properties_by_id
                const safeBgData = getSafePropertyData(allData)
                setProperty({
                  ...safeBgData,
                  lines_preloading: false,
                })
              })
            }
            if(debugTimeout){
              setTimeout(() => {
                loadAdditionalLines()
              }, debugTimeout);
            } else {
              loadAdditionalLines()
            }
          }
        }

        const safeData = getSafePropertyData(data)
        
        // Check if property data is valid before rendering
        if (!safeData || !safeData.unit_types || safeData.unit_types.length === 0) {
          setFetchError(true)
          setLoading(false)
          return
        }
        
        setProperty(safeData)

        const defaultLine =
        unit_name ? 
          safeData.unit_types.find((u) => u.units.find((unit) => unit.name === unit_name)) || safeData.unit_types[0]
        :
          safeData.unit_types.find((u) =>
            line_id
              ? u.id === Number(line_id)
              : safeConfigurationOptions(u.unit_types_configuration_options).default_selected,
          ) || safeData.unit_types[0]

        setSelectedLine(defaultLine)
        setSelectedLevel(defaultLine.unit_type_levels[0])

        // Set global data to store.
        setData({
          selectedLine: defaultLine,
          property: safeData,
          selectedLevel: defaultLine.unit_type_levels[0],
        })

        set({
          hide_disclaimer: safeData.property_name?.toLowerCase().includes('evoq') ? false : hide_disclaimer,
        })

        captureEvent(analyticsEvents.WELCOME_SCREEN_VIEWED)

        setLoading(false)
        } catch (error) {
          console.error('Failed to fetch property:', error)
          setFetchError(true)
          setLoading(false)
        }
      })()
    }
  }, [property_id, property, setData, external_property_id, line_id, unit_name, fetchError])

  const [modelLoading, setModelLoading] = useState(true)

  useEffect(() => {
    if (selectedLevel && selectedPackage && !selectedPlan) {
      setSelectedPlan(selectedPackage.unit_plans.find((p) => p.unit_type_level.id === selectedLevel.id))
    }
  }, [selectedLevel, selectedPlan, selectedPackage])

  const [infoShown, setInfoShown] = useState(false)

  const viewShown = selectedPackage && selectedLine && selectedLevel

  const showLevelsPicker = selectedLine?.unit_type_levels.length > 1

  const [guideShown, setGuideShown] = useState(null)

  useEffect(() => {
    if (guideShown === null && !modelLoading) {
      setGuideShown(true)
    }
    if (isMobileLandscape && guideShown === true && modelLoading) {
      setGuideShown(false)
    }
  }, [modelLoading, guideShown, isMobileLandscape])

  const onGuideEnd = useCallback(() => {
    setGuideShown(false)
  }, [])

  function shouldShowGlobalApplyNowButton() {
    if (property?.property_name.toLowerCase().indexOf('oliver') > -1) {
      return true
    }
    if (property?.property_name.toLowerCase().indexOf('edmond') > -1) {
      return true
    }
    return (
      showApplyNowButton(line_id) &&
      (mimicVariant === variants.ev || (isSWApp && property?.property_name.toLowerCase().indexOf('evoq') > -1))
    )
  }

  const showLinePill = !single_line || (single_line && !selectedLine?.unit_types_configuration_options?.hide_line_pill)

  const controls = (
    <>
      {!hide_line && (
        <VariantWrapper hide={[variants.of, variants.br]}>
          {showLinePill && (
            <>
              <ScreenType>
                <LinePicker
                  className={`absolute hidden lg:block bottom-[46px] lg:top-[24px] left-[24px] ${
                    showLevelsPicker ? 'w-[calc(100%-48px-52px-12px)]' : 'w-[calc(100%-48px)]'
                  } lg:w-max`}
                  onLineSelected={({ data }) => {
                    setSelectedLine(data)
                    setSelectedLevel(data.unit_type_levels[0])
                    setSelectedPackage(data.packages.find((p) => p.show_by_default) || data.packages[0])
                    captureEvent(analyticsEvents.FLOOR_PLAN_SELECTED, { selectedLine: data })
                  }}
                  onFloorPlanSelected={(fp, line, pack, level) => {
                    captureEvent(analyticsEvents.FLOOR_PLAN_SELECTED, { selectedLine: line })
                    setSelectedLine(line)
                    setSelectedLevel(level)
                    setSelectedPackage(line.packages.find((p) => p.show_by_default) || line.packages[0])
                    setSelectedPlan(fp)
                  }}
                  selectedPlan={selectedPlan}
                  selectedLine={selectedLine}
                  property={property}
                />
              </ScreenType>
              <ScreenType type="tv">
                <TVLinePicker
                  className={`absolute hidden lg:block bottom-[46px] lg:top-[24px] left-[24px] ${
                    showLevelsPicker ? 'w-[calc(100%-48px-52px-12px)]' : 'w-[calc(100%-48px)]'
                  } lg:w-max`}
                  onLineSelected={({ data }) => {
                    setSelectedLine(data)
                    setSelectedLevel(data.unit_type_levels[0])
                    setSelectedPackage(data.packages.find((p) => p.show_by_default) || data.packages[0])
                    captureEvent(analyticsEvents.FLOOR_PLAN_SELECTED, { selectedLine: data })
                  }}
                  onFloorPlanSelected={(fp, line, pack, level) => {
                    captureEvent(analyticsEvents.FLOOR_PLAN_SELECTED, { selectedLine: line })
                    setSelectedLine(line)
                    setSelectedLevel(level)
                    setSelectedPackage(line.packages.find((p) => p.show_by_default) || line.packages[0])
                    setSelectedPlan(fp)
                  }}
                  selectedPlan={selectedPlan}
                  selectedLine={selectedLine}
                  property={property}
                />
              </ScreenType>
            </>
          )}
        </VariantWrapper>
      )}
      <VariantWrapper show={variants.of}>
        {/* <div className="fixed landscape:hidden lg:landscape:inline-block top-[92px] lg:top-auto lg:bottom-[42px] lg:left-[24px] z-[40]">
          {' '}
          <OfficesDesignPicker
            onPackageSelected={(p) => {
              setSelectedPackage(p)
              if (selectedPackage) {
                captureEvent(analyticsEvents.PACKAGE_SELECTED, { selectedPackage: p })
              }
            }}
            packages={packages}
            defaultPackage={package_id}
            selectedLevel={selectedLevel}
            selectedLine={selectedLine}
            selectedPackage={selectedPackage}
          />
        </div> */}
        {/* <div className="fixed hidden lg:inline-block top-[24px] left-[24px]">
          {' '}
          <OfficesPropertyLogo />
        </div> */}
        {/* {!hide_line && (
          <div className="fixed hidden lg:inline-block top-[24px] right-[24px]">
            {' '}
            <OfficesLinePicker
              onLineSelected={({ data }) => {
                setSelectedLine(data)
                setSelectedLevel(data.unit_type_levels[0])
                setSelectedPackage(data.packages[0])
                captureEvent(analyticsEvents.FLOOR_PLAN_SELECTED, { selectedLine: data })
              }}
              property={property}
              selectedLevel={selectedLevel}
              selectedLine={selectedLine}
            />
          </div>
        )} */}
        <div
          className="fixed landscape:bottom-[24px] portrait:bottom-[40px] lg:landscape:bottom-[54px]
         lg:portrait:bottom-[54px] landscape:left-[228px] portrait:left-[24px] 
         lg:portrait:left-auto lg:right-[24px] lg:landscape:left-auto z-[100] 
         landscape:w-[calc(100%-252px)] portrait:w-[calc(100%-48px)] 
         lg:landscape:w-auto lg:portrait:w-auto  
         flex items-center justify-between"
        >
          <div className="mr-[16px] z-[1]">
            <OfficesGallery packages={packages} selectedLine={selectedLine} selectedPackage={selectedPackage} />
          </div>
          <ScreenType>
            <Reset3DViewer className="h-[40px] w-[40px]" />
          </ScreenType>
        </div>
      </VariantWrapper>
      <VariantWrapper hide={variants.of}>
        {!welcomeModalShown && (
          <div
            className={`lg:z-[30] absolute landscape:bottom-[16px]  flex items-center ${(() => {
              if (isBrodskyApp || isSFApp) {
                return '!bottom-[12px] lg:!bottom-[16px] left-[16px] lg:left-[24px] !w-[calc(100%-32px)] lg:!w-[calc(100%-48px)]'
              }
              return `lg:!bottom-[24px] lg:landscape:bottom-[24px] right-[24px] ${
                !as_staging ? 'portrait:bottom-[95px]' : ' portrait:bottom-[24px]'
              }`
            })()}  ${showLevelsPicker ? 'w-[calc(100%-216px)]' : 'w-[calc(100%-48px)]'} ${
              isBrodskyApp || isSFApp ? '!justify-between' : 'justify-between'
            }  ${isWideMode ? 'lg:justify-start' : ''}`}
          >
            {isStagedWithCortPackage(selectedPackage) && (
              <VariantWrapper hide={[variants.br, variants.sf]}>
                {!as_staging && (
                  <div className={`order-2 ${isWideMode ? 'lg:mr-[16px] lg:order-1' : ''}`}>
                    {selectedPackage && (
                      <>
                        <ScreenType>
                          <PackageInfo
                            selectedPackage={selectedPackage}
                            selectedLine={selectedLine}
                            onShown={setInfoShown}
                          />
                        </ScreenType>
                        <ScreenType type="tv">
                          <TVPackageInfo
                            className="bottom-[calc(64px+24px)]"
                            selectedPackage={selectedPackage}
                            selectedLine={selectedLine}
                            onShown={setInfoShown}
                          />
                        </ScreenType>
                      </>
                    )}
                  </div>
                )}
              </VariantWrapper>
            )}
            <VariantWrapper show={[variants.br, variants.sf]}>
              <span className="text-[12px] lg:text-[16px] mt-[4px] lg:mt-[2px] flex items-start">
                <SparxLogo height="16px" />
              </span>
            </VariantWrapper>
            <div className={`z-[30] ${isWideMode ? 'lg:order-2' : ''} flex`}>
              <ScreenType>
                <Reset3DViewer />
              </ScreenType>
              <ScreenType type="tv">
                <TVReset3DViewer />
              </ScreenType>
              <VariantWrapper show={[variants.br, variants.sf]}>
                <FullScreenToggle className="ml-[16px] lg:order-3" />
              </VariantWrapper>
            </div>
          </div>
        )}
      </VariantWrapper>

      <VariantWrapper hide={[variants.br, variants.of]}>
        <div className="absolute left-0 bottom-[30px] items-center hidden lg:flex w-full justify-center">
          {!modelLoading && isEWApp && (
            <div className="z-[30]">
              <EversetLogo />
            </div>
          )}
          <ScreenType>
            {!modelLoading && !isSFApp && !isEWApp && !isOfficesApp && !isAvalonApp && !hide_disclaimer && (
              <div className="relative z-[30]">
                <Disclaimer selectedPackage={selectedPackage} />
              </div>
            )}
            {!modelLoading && isAvalonApp && (
              <div className="relative z-[30]">
                <Disclaimer selectedPackage={selectedPackage} />
              </div>
            )}
          </ScreenType>
        </div>
      </VariantWrapper>
      {showLevelsPicker && (
        <>
          <ScreenType>
            <LevelPicker
              onLevelSelected={(l) => {
                captureEvent(analyticsEvents.LEVEL_SELECTED)
                setSelectedLevel(l.data)
              }}
              selectedLine={selectedLine}
              selectedLevel={selectedLevel}
              className="absolute bottom-[16px] portrait:bottom-[95px] lg:!bottom-[24px] left-[24px] w-max lg:z-[10]"
            />
          </ScreenType>
          <ScreenType type="tv">
            <TVLevelPicker
              onLevelSelected={(l) => {
                captureEvent(analyticsEvents.LEVEL_SELECTED)
                setSelectedLevel(l.data)
              }}
              selectedLine={selectedLine}
              selectedLevel={selectedLevel}
              className="absolute bottom-[16px] portrait:bottom-[95px] lg:!bottom-[24px] left-[24px] w-max lg:z-[10]"
            />
          </ScreenType>
        </>
      )}

      {!as_staging && (
        <>
          <VariantWrapper hide={[variants.br, variants.sf]}>
            <MobileBottomSection
              modelLoading={modelLoading}
              className={`absolute w-full ${(() => {
                if (isOfficesApp || hide_footer) {
                  return 'bottom-[0px]'
                }
                return 'bottom-[54px]'
              })()} landscape:hidden portrait:block lg:portrait:hidden lg:landscape:hidden`}
            />
          </VariantWrapper>
          {!hide_footer && (
            <VariantWrapper hide={[variants.of, variants.br, variants.sf]}>
              <MobileFooter
                modelLoading={modelLoading}
                className="absolute w-full bottom-0 landscape:hidden portrait:block lg:portrait:hidden lg:landscape:hidden"
              />
            </VariantWrapper>
          )}
        </>
      )}
      <VariantWrapper hide={[variants.of, variants.br, variants.sf]}>
        <div className={`landscape:fixed absolute right-[24px] ${isAvalonApp ? 'landscape:bottom-[16px] landscape:top-auto' : 'top-[16px]'} portrait:bottom-[0] portrait:flex portrait:justify-end portrait:right-[24px] portrait:items-center portrait:top-auto landscape:z-[200] portrait:z-[40] portrait:h-[56px] lg:hidden`}>
          {!as_staging && (
            <ContactUs className="landscape:w-max landscape:!rounded-[6px] lg:hidden !px-[20px] !py-[5px] " />
          )}
        </div>
      </VariantWrapper>
      {shouldShowGlobalApplyNowButton() && (
        <div className="right-[24px] landscape:bottom-[16px] portrait:bottom-[24px] lg:!bottom-[24px] absolute z-[40]">
          <ApplyNowButton />
        </div>
      )}
    </>
  )

  useEffect(() => {
    setData({
      selectedLevel,
      selectedLine,
      selectedPlan,
      selectedPackage,
      property,
      setData,
      appLoaded: !loading,
    })
  }, [selectedLevel, selectedLine, selectedPlan, selectedPackage, property, setData, loading])

  useSendOnceWithDeps(
    selectedPackage,
    useCallback(() => {
      captureEvent(analyticsEvents.HOME_SCREEN_VIEWED)
    }, []),
  )

  const onStart = useCallback(() => {
    if (welcomeModalShown) {
      setWelcomeModalShown(false)
    }
  }, [welcomeModalShown])

  useEffect(() => {
    if (!welcomeModalShown && modelLoading) {
      setShowCenterLoader(true)
    }
  }, [welcomeModalShown, modelLoading])

  const [showCenterLoader, setShowCenterLoader] = useState(false)

  const onModelLoadingChange = useCallback((loading) => {
    if (!loading) {
      setShowCenterLoader(false)
    }
    setModelLoading(loading)
  }, [])

  const minimalPackages = isOfficesApp || isSFApp || hide_vendor

  return (
    <>
      <VariantWrapper hide={[variants.br, variants.sf]}>
        {property && (
          <MobileSidebar
            packages={packages}
            property={property}
            selectedLine={selectedLine}
            onPackageSelected={(p) => {
              if(p.id === selectedPackage.id){
                return
              }
              setSelectedPackage(p)
              if (selectedPackage) {
                captureEvent(analyticsEvents.PACKAGE_SELECTED, { selectedPackage: p })
              }
            }}
            selectedLevel={selectedLevel}
            selectedPackage={selectedPackage}
            selectedPlan={selectedPlan}
            onLineSelected={({ data }) => {
              setSelectedLine(data)
              setSelectedLevel(data.unit_type_levels[0])
            }}
            onFloorPlanSelected={(fp, line, pack, level) => {
              setSelectedLine(line)
              setSelectedLevel(level)
              setSelectedPackage(pack)
              setSelectedPlan(fp)
            }}
            className="landscape:flex portrait:hidden lg:landscape:hidden"
          />
        )}
      </VariantWrapper>
      <div className={`h-full w-full relative flex flex-col lg:block`} id="view">
        {property && !loading && (
          <>
            {!hide_packages && (
            <VariantWrapper hide={[variants.br]}>
              <ScreenType>
                <DesignPicker
                  packages={packages}
                  onPackageSelected={(p) => {
                    if(p.id === selectedPackage.id){
                      return
                    }
                    setSelectedPackage(p)
                    if (selectedPackage) {
                      captureEvent(analyticsEvents.PACKAGE_SELECTED, { selectedPackage: p })
                    }
                  }}
                  minimal={minimalPackages}
                  defaultPackage={package_id}
                  selectedLevel={selectedLevel}
                  selectedLine={selectedLine}
                  selectedPackage={selectedPackage}
                  className={`overflow-hidden landscape:hidden ${
                    isSFApp ? 'sm:landscape:flex' : 'lg:landscape:flex'
                  } portrait:flex absolute ${
                    isOfficesApp || (isMobile && isSFApp)
                      ? 'top-[24px] lg:top-auto lg:bottom-[12px] lg:left-[24px]'
                      : 'top-[90px] lg:top-[24px] left-0 lg:left-[auto] lg:right-[24px]'
                  } lg:z-[40] ${infoShown ? 'z-[150]' : ''}`}
                />
              </ScreenType>
              <ScreenType type="tv">
                <TVDesignPicker
                  className=" absolute bottom-[24px] right-[24px] z-[100]"
                  packages={packages}
                  selectedPackage={selectedPackage}
                  onPackageSelected={(p) => {
                    if(p.id === selectedPackage.id){
                      return
                    }
                    setSelectedPackage(p)
                    if (selectedPackage) {
                      captureEvent(analyticsEvents.PACKAGE_SELECTED, { selectedPackage: p })
                    }
                  }}
                />
              </ScreenType>
            </VariantWrapper>
            )}
            {/* <VariantWrapper show={variants.of}>
              <OfficesMobileHeader
                onLineSelected={({ data }) => {
                  setSelectedLine(data)
                  setSelectedLevel(data.unit_type_levels[0])
                  setSelectedPackage(data.packages[0])
                }}
                property={property}
                selectedLine={selectedLine}
                className="landscape:hidden portrait:flex lg:portrait:hidden lg:landscape:hidden"
              />
            </VariantWrapper> */}
            <VariantWrapper hide={[variants.of, variants.br, variants.sf]}>
              <MobileHeader
                showLinePill={showLinePill}
                onLineSelected={({ data }) => {
                  setSelectedLine(data)
                  setSelectedLevel(data.unit_type_levels[0])
                  setSelectedPackage(data.packages[0])
                }}
                onFloorPlanSelected={(fp, line, pack, level) => {
                  setSelectedLine(line)
                  setSelectedLevel(level)
                  setSelectedPackage(line.packages[0])
                  setSelectedPlan(fp)
                }}
                selectedPlan={selectedPlan}
                selectedLine={selectedLine}
                property={property}
                className="landscape:hidden portrait:flex lg:portrait:hidden lg:landscape:hidden"
              />
            </VariantWrapper>
            {controls}
          </>
        )}

        {viewShown && (
          <View
            selectedPackage={selectedPackage}
            selectedLevel={selectedLevel}
            selectedLine={selectedLine}
            className="h-[calc(100%-70px)] lg:h-full"
            showLoader={!welcomeModalShown}
            showCenterLoader={showCenterLoader}
            onModelLoadingChange={onModelLoadingChange}
          />
        )}
        <VariantWrapper hide={[variants.of, variants.br, variants.sf]}>
          <div className="absolute left-[24px] top-[0] portrait:hidden lg:hidden">
            {(() => {
              if (isEWApp) {
                return (
                  <div className="mt-[20px]">
                    <EversetLogo />
                  </div>
                )
              }
              if (isPDApp) {
                return (
                  <div className="flex items-center">
                    <CortLogo />
                    <div className="ml-[16px]">
                      <PropertyLogo />
                    </div>
                  </div>
                )
              }
              if (isSWApp) {
                return <VendorLogo className="!h-[20px] mt-[24px]" />
              }
              if (isAvalonApp) {
                return null
              }
              return <CortLogo />
            })()}
          </div>
        </VariantWrapper>
        {!welcomeModalShown && guideShown && !subsequent_open && (
          <>
            <ScreenType>
              <div
                className={`absolute ${
                  isBrodskyApp || isSFApp
                    ? 'bottom-[120px] portrait:bottom-[140px] lg:bottom-[150px] opacity-60'
                    : isAvalonApp
                    ? 'bottom-[160px] portrait:bottom-[300px]'
                    : 'bottom-[72px] portrait:bottom-[198px]'
                }  left-[50%] ml-[-100px] portrait:ml-[-100px] lg:!bottom-[150px] z-[1]`}
              >
                <Guide wide={(isBrodskyApp || isSFApp) && isMobileLandscape} onEnd={onGuideEnd} />
              </div>
            </ScreenType>
            <ScreenType type="tv">
              <div
                className={`absolute ${
                  isBrodskyApp || isSFApp
                    ? 'bottom-[150px] portrait:bottom-[160px] lg:bottom-[150px] opacity-60'
                    : isAvalonApp
                    ? 'bottom-[200px] portrait:bottom-[340px]'
                    : 'bottom-[150px] portrait:bottom-[198px]'
                }  left-[50%] ml-[-100px] portrait:ml-[-100px] lg:!bottom-[220px] z-[1]`}
              >
                <Guide wide={(isBrodskyApp || isSFApp) && isMobileLandscape} onEnd={onGuideEnd} />
              </div>
            </ScreenType>
          </>
        )}
        {debug && <DebugControls className="absolute top-[50%] left-0 translate-y-[-50%]" />}
        {!welcomeModalHiddenFromParams && (
          <WelcomeModal
            shown={welcomeModalShown}
            modelLoaded={!modelLoading && !!property}
            metadataLoaded={!!property}
            onStart={onStart}
          />
        )}
        {property && !loading && <ModelsPrefetcher selectedLine={selectedLine} selectedLevel={selectedLevel} />}
      </div>
      {fetchError && <AccessRestrictedModal message="This resource can't be loaded" />}
      {property && !loading && property.is_active === false && <AccessRestrictedModal />}
    </>
  )
}

export default Preview
